import { createContext, useContext, useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Milestone 2 · Role & Permission Framework — client cache of the current
 * user's permission map. The backend is the source of truth (/api/roles/me/permissions);
 * this context just caches it in memory for cheap has() checks in components.
 */
const PermCtx = createContext(null);

export function PermissionsProvider({ children }) {
  const { user } = useAuth();
  const [perms, setPerms] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) { setPerms(null); return; }
    let cancel = false;
    setLoading(true);
    api.get("/roles/me/permissions")
      .then((r) => { if (!cancel) setPerms(r.data.permissions || {}); })
      .catch(() => { if (!cancel) setPerms({}); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [user?.user_id, user?.role]);

  const value = useMemo(() => {
    const has = (module, action = "view") => {
      // MD / super admin bypass — check role directly to avoid a flash while perms fetch
      if (user?.role === "managing_director" || user?.role === "super_admin") return true;
      if (!perms) return false;
      const actions = perms[module];
      if (!actions) return false;
      return actions.includes(action);
    };
    return { perms, loading, has, role: user?.role };
  }, [perms, loading, user?.role]);

  return <PermCtx.Provider value={value}>{children}</PermCtx.Provider>;
}

export function usePermissions() {
  const v = useContext(PermCtx);
  if (!v) throw new Error("usePermissions must be inside PermissionsProvider");
  return v;
}
